import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  MapPin,
  User,
  Phone,
  FileText,
  ShoppingBag,
  Check,
  Loader2,
  AlertCircle,
  Building2,
} from 'lucide-react';
import { cartApi, orderApi, shopApi, tokenStorage } from '@/user/api';
import type { CartEntity, ProductVO } from '@/types/api-entity';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface CartDisplayItem {
  productId: number;
  productName: string;
  mainImage?: string;
  price: number;
  quantity: number;
  unit?: string;
}

export default function Checkout() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartDisplayItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [receiveAddress, setReceiveAddress] = useState('');
  const [receiveContact, setReceiveContact] = useState('');
  const [receivePhone, setReceivePhone] = useState('');
  const [remark, setRemark] = useState('');

  const isLoggedIn = tokenStorage.isLoggedIn();

  const loadCart = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!isLoggedIn) {
        setError('请先登录后再结算');
        setLoading(false);
        return;
      }
      const cartRes = await cartApi.getCartList();
      if (cartRes.code === 200 && cartRes.data) {
        const backendItems = cartRes.data;
        if (backendItems.length === 0) {
          setError('购物车为空，无法结算');
          setLoading(false);
          return;
        }
        const productPromises = backendItems.map((item) =>
          shopApi.getProductDetail(item.productId).catch(() => null)
        );
        const productResults = await Promise.all(productPromises);
        const items: CartDisplayItem[] = backendItems.map((cartItem, idx) => {
          const product = productResults[idx]?.data;
          return {
            productId: cartItem.productId,
            productName: product?.productName || `商品#${cartItem.productId}`,
            mainImage: product?.mainImage,
            price: product?.price || 0,
            quantity: cartItem.quantity,
            unit: product?.unit,
          };
        });
        setCartItems(items);
      }
    } catch (err) {
      setError('加载购物车失败');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const totalAmount = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleSubmit = async () => {
    if (!receiveAddress.trim()) {
      setError('请输入收货地址');
      return;
    }
    if (!receiveContact.trim()) {
      setError('请输入收货联系人');
      return;
    }
    if (!receivePhone.trim()) {
      setError('请输入收货电话');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const res = await orderApi.createOrder({
        receiveAddress: receiveAddress.trim(),
        receiveContact: receiveContact.trim(),
        receivePhone: receivePhone.trim(),
        remark: remark.trim() || undefined,
      });
      if (res.code === 200 && res.data) {
        const orderCount = res.data.length;
        alert(`下单成功！共生成 ${orderCount} 个订单，请等待平台审核。`);
        navigate('/');
      } else {
        setError(res.message || '下单失败');
      }
    } catch (err: any) {
      const msg = err.message || '下单失败，请稍后重试';
      if (msg.includes('采购商') || msg.includes('企业资料')) {
        setError('buyer_profile_required');
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 pt-24 pb-8 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-[#1c5bff] animate-spin mx-auto mb-4" />
            <p className="text-gray-500">加载中...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  const getFirstImage = (mainImage: string | string[] | undefined | null): string => {
    if (!mainImage) return 'https://via.placeholder.com/80';
    
    let firstUrl = '';
    if (Array.isArray(mainImage)) {
      firstUrl = mainImage[0] || '';
    } else if (typeof mainImage === 'string') {
      const urls = mainImage.split(',');
      firstUrl = urls.length > 0 ? urls[0].trim() : '';
    }
    
    return firstUrl || 'https://via.placeholder.com/80';
  };
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 pt-24 pb-8">
        <div className="section-container max-w-4xl">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingBag className="w-6 h-6 text-[#1c5bff]" />
              确认订单
            </h1>
          </div>

          {error === 'buyer_profile_required' ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 text-amber-700 text-sm mb-3">
                <Building2 className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">未找到采购商信息，请先完善企业资料</span>
              </div>
              <p className="text-amber-600 text-sm mb-3">
                您需要先完善采购商企业资料，才能在商城下单。完善后需等待平台审核通过。
              </p>
              <Link
                to="/console/profile"
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm"
              >
                <Building2 className="w-4 h-4" />
                去完善企业资料
              </Link>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          ) : null}

          {!isLoggedIn ? (
            <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
              <p className="text-gray-500 mb-4">请先登录后再结算</p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors"
              >
                去登录
              </Link>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
              <p className="text-gray-500 mb-4">购物车为空，无法结算</p>
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors"
              >
                去购物
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Cart Items & Address */}
              <div className="lg:col-span-2 space-y-6">
                {/* Cart Items */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-[#1c5bff]" />
                    商品清单
                  </h2>
                  <div className="space-y-4">
                    {cartItems.map((item) => (
                      <div
                        key={item.productId}
                        className="flex gap-4 py-4 border-b border-gray-100 last:border-0"
                      >
                        <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={getFirstImage(item.mainImage)}
                            alt={item.productName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.productName}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            单价: {formatPrice(item.price)} × {item.quantity} {item.unit || '件'}
                          </p>
                          <p className="text-[#1c5bff] font-bold mt-1">
                            {formatPrice(item.price * item.quantity)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Address Form */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#1c5bff]" />
                    收货信息
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        收货地址 <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={receiveAddress}
                          onChange={(e) => setReceiveAddress(e.target.value)}
                          placeholder="请输入详细收货地址"
                          className="w-full h-11 pl-10 pr-4 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          收货联系人 <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={receiveContact}
                            onChange={(e) => setReceiveContact(e.target.value)}
                            placeholder="请输入联系人姓名"
                            className="w-full h-11 pl-10 pr-4 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          收货电话 <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="tel"
                            value={receivePhone}
                            onChange={(e) => setReceivePhone(e.target.value)}
                            placeholder="请输入联系电话"
                            className="w-full h-11 pl-10 pr-4 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        订单备注
                      </label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <textarea
                          value={remark}
                          onChange={(e) => setRemark(e.target.value)}
                          placeholder="如有特殊要求请填写备注（选填）"
                          rows={3}
                          className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all resize-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">费用明细</h2>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">商品总额</span>
                      <span className="text-gray-900">{formatPrice(totalAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">运费</span>
                      <span className="text-green-600">免运费</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">平台服务费</span>
                      <span className="text-gray-400 text-xs">下单后计算</span>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">应付总额</span>
                      <span className="text-2xl font-bold text-[#1c5bff]">
                        {formatPrice(totalAmount)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      实际支付金额以订单生成后为准（含平台服务费）
                    </p>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full py-3 bg-[#1c5bff] text-white font-semibold rounded-lg hover:bg-[#1547cc] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        提交中...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        确认下单
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => navigate('/cart')}
                    className="mt-3 w-full py-3 border border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    返回购物车
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
